/**
 * ImFrame 帧协议（story AC-2 / §架构契约 2）。字段名与服务端 `ImFrame.java` 逐字对齐。
 * 单一 DTO，JSON 文本帧；序列化省略 null 字段，解析容忍未知字段。
 *
 * 出站（client→server）：PING / CHAT / DISCONNECT / APP_STATE
 * 入站（server→client）：PONG / CONNECTED / DISCONNECTED / FORCE_LOGOUT / ERROR /
 *                        ACK / MESSAGE / SYNC_START / SYNC_BATCH / SYNC_COMPLETE
 */

export type ImFrameType =
  | 'PING'
  | 'PONG'
  | 'CONNECTED'
  | 'DISCONNECT'
  | 'DISCONNECTED'
  | 'FORCE_LOGOUT'
  | 'ERROR'
  | 'CHAT'
  | 'ACK'
  | 'MESSAGE'
  | 'SYNC_START'
  | 'SYNC_BATCH'
  | 'SYNC_COMPLETE'
  | 'APP_STATE';

/** SYNC_BATCH 内每条冷数据（auditStatus 仅同步帧带，JS 忽略）。 */
export interface SyncMessage {
  serverMsgId?: string;
  conversationId?: string;
  senderId?: string;
  content?: string;
  sentAtMs?: number;
  auditStatus?: string;
}

export interface ImFrame {
  type: ImFrameType | string;
  ts?: number;
  userId?: string;
  serverTime?: number;
  code?: string;
  msg?: string;
  clientMsgId?: string;
  serverMsgId?: string;
  conversationId?: string;
  content?: string;
  senderId?: string;
  sentAtMs?: number;
  recipientOnline?: boolean;
  riskKeywords?: string[];
  totalCount?: number;
  totalBatches?: number;
  batchSeq?: number;
  batchSize?: number;
  syncedCount?: number;
  lastSyncedAtMs?: number;
  messages?: SyncMessage[];
  state?: 'FOREGROUND' | 'BACKGROUND';
}

/** WS 子协议（new WebSocket(url, IM_SUBPROTOCOL)）。 */
export const IM_SUBPROTOCOL = 'workability-im-v1';

/** 服务端 / 引擎错误码（story §架构契约 2f）。 */
export const ImCode = {
  HANDSHAKE_UNAUTHORIZED: '040001',
  KICKED_BY_NEW_CONNECTION: '040003',
  FRAME_FORMAT_INVALID: '040004',
  CHAT_INVALID_CONTENT: '040201',
  CHAT_CONVERSATION_NOT_FOUND: '040202',
  CHAT_FORBIDDEN: '040203',
  CHAT_REJECTED_BY_AUDIT: '040206',
} as const;

/** 内容最大长度（服务端 CHAT_CONTENT_MAX_LENGTH）。 */
export const CHAT_CONTENT_MAX_LENGTH = 500;

/** 编码为 WS 文本帧（NON_NULL：undefined 字段经 JSON.stringify 天然省略）。 */
export function encodeFrame(frame: ImFrame): string {
  return JSON.stringify(frame);
}

/**
 * 解析入站文本帧。JSON 非法 / 非对象 / type 缺失或空 → null（调用方丢弃，story AC-2 容错）。
 */
export function decodeFrame(raw: string): ImFrame | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const frame = parsed as ImFrame;
  if (typeof frame.type !== 'string' || frame.type.trim() === '') return null;
  return frame;
}

// ---- 出站帧工厂 ----
export function pingFrame(ts: number): ImFrame {
  return { type: 'PING', ts };
}

export function chatFrame(clientMsgId: string, conversationId: string, content: string, ts: number): ImFrame {
  return { type: 'CHAT', clientMsgId, conversationId, content, ts };
}

export function disconnectFrame(): ImFrame {
  return { type: 'DISCONNECT' };
}

export function appStateFrame(state: 'FOREGROUND' | 'BACKGROUND'): ImFrame {
  return { type: 'APP_STATE', state };
}
